"use client";

import NextLink from "next/link";
import { Button as ReactAriaButton } from "react-aria-components";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { Button, type ButtonProperties } from "@jetblue/kit/button";
import { Popover } from "@jetblue/kit/popover";

import { Ad, type AdProperties } from "../ad";
import { CallToActions } from "./call-to-actions";
import { constructReturnURL } from "../../../../../helpers/links/return-url";

import type { UnauthenticatedCallToActionsProperties } from "./call-to-actions";

export type UnauthenticatedProperties = {
  ad: AdProperties;
  button: {
    label: string;
    logo?: string | undefined;
  };
  callToActions: UnauthenticatedCallToActionsProperties;
  signIn: {
    href: string;
    label: string;
    variant?: ButtonProperties["variant"];
  };
};
export const Unauthenticated = ({
  ad,
  button: { label, logo },
  callToActions,
  signIn,
}: UnauthenticatedProperties) => {
  const signInButtonVariant = signIn.variant ?? "primary";

  const pathname = usePathname();
  const [href, setHref] = useState(constructReturnURL(signIn.href));

  useEffect(() => {
    setHref(constructReturnURL(signIn.href));
  }, [signIn.href, pathname]);

  return (
    <Popover
      className="
        size-full overflow-auto rounded-none border-none
        bg-background-surface-container p-0

        md:h-unset md:w-[375px] md:rounded-b-md md:shadow-jb-2
      "
      offset={0}
      placement="bottom right"
      showOverlayArrow={false}
      triggerButton={
        <ReactAriaButton
          aria-haspopup="dialog"
          className="
            relative flex h-full shrink-0 grow-0 cursor-pointer items-center
            justify-center gap-2 px-2 text-foreground-primary-variant

            after:absolute after:bottom-0 after:h-1 after:w-0
            after:bg-background-accent after:transition-all after:content-['_']

            hover:after:w-full

            focus-visible:outline-0

            focus-visible:after:absolute focus-visible:after:bottom-0
            focus-visible:after:h-1 focus-visible:after:w-full
            focus-visible:after:bg-background-accent
            focus-visible:after:transition-all focus-visible:after:content-['_']

            aria-expanded:after:w-full

            [html[data-header-bg-white=false]_&]:text-foreground-on-primary
          "
          data-fs-element="main-nav-menu-button-user-unauthenticated"
        >
          {logo && (
            <span className="relative size-8">
              {/* <NextImage alt="" fill role="presentation" src={logo} /> */}
              <svg
                height="32"
                role="presentation"
                viewBox="0 0 32 32"
                width="32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fill="currentColor" fillRule="nonzero">
                  <path d="M16 19c5.523 0 10 4.214 10 9.412 0 .042-.921.075-2.369.1l-.006-1.57c-.7-3.323-3.73-5.823-7.364-5.938L16 21c-3.658 0-6.753 2.418-7.568 5.695l-.039 1.817C6.932 28.488 6 28.454 6 28.412 6 23.214 10.477 19 16 19" />
                  <path d="M16 6.5a7 7 0 1 0 0 14 7 7 0 0 0 0-14m0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10" />
                  <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0m0 2c7.732 0 14 6.268 14 14s-6.268 14-14 14S2 23.732 2 16 8.268 2 16 2" />
                </g>
              </svg>
            </span>
          )}

          <span
            className="
              sr-only text-foreground-primary-variant

              lg:not-sr-only

              [html[data-header-bg-white=false]_&]:text-foreground-on-primary
            "
          >
            {label}
          </span>
        </ReactAriaButton>
      }
    >
      <div className="p-6">
        <Button
          as={NextLink}
          data-fs-element="user-unauthenticated-sign-in-link"
          href={href}
          isFullWidth
          size="large"
          variant={signInButtonVariant}
        >
          {signIn.label}
        </Button>
      </div>

      <CallToActions {...callToActions} />

      <Ad {...ad} dataAttributePrefix="user-unauthenticated" />
    </Popover>
  );
};
